import {
  pgTable,
  text,
  boolean,
  timestamp,
  integer,
  index,
  uniqueIndex,
  foreignKey,
  jsonb,
} from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import type { ExternalRestreamTarget } from '@masters-club/types';
import {
  livestreamSource,
  livestreamStatus,
  livestreamVisibility,
  notificationChannel,
} from './enums';
import { adminUser } from './admin';
import { user } from './user';
import { file } from './files';
import { chatRoom } from './messaging';
import { moduleItem } from './course';

export interface LivestreamInteractionOption {
  id: string;
  label: string;
}

export interface LivestreamInteractionTelemetry {
  userAgent: string | null;
  ipHash: string | null;
}

export interface LivestreamNotificationButton {
  text: string;
  url: string;
}

export type LivestreamNotificationSubscriptionSource = 'MANUAL' | 'AUTO';

// ==========================================
// LIVESTREAM — основная запись о трансляции
// ==========================================
// Конфигурация прямой трансляции: расписание, статус, поток RTMP-ключа,
// привязка к хост-админу, чат-комнате и (опционально) файлу записи.
// `streamKey` — секрет, генерируется сервис-слоем при создании трансляции
// и используется RTMP-сервером для аутентификации OBS-публикации.
//
// Переводы title/description живут в универсальной таблице Translation
// (entityType = 'LIVESTREAM', entityId = Livestream.id). Поля title/description
// в этой таблице — исходник (на языке автора) и fallback при отсутствии перевода.
export const livestream = pgTable(
  'Livestream',
  {
    id: integer().primaryKey().generatedAlwaysAsIdentity(),

    title: text().notNull(),
    description: text(),

    visibility: livestreamVisibility().default('PRIVATE').notNull(),
    status: livestreamStatus().default('SCHEDULED').notNull(),

    // Хост эфира — админ, ведущий трансляцию. RESTRICT чтобы нельзя было
    // удалить админа, если у него есть трансляции (сначала реассайн).
    hostAdminId: integer().notNull(),

    // RTMP publish key — секрет, уникальный на трансляцию.
    streamKey: text().notNull(),

    // Активный источник входящего потока, отдаваемый зрителям.
    // OBS пушит на path `live/<streamKey>`, ZOOM — на `live/<streamKey>-zoom`.
    // Оба могут публиковаться одновременно; это поле — какой из них видят зрители.
    // Меняется hot-swap'ом из админки (см. livestream-source.service).
    activeSource: livestreamSource().default('OBS').notNull(),

    // Чат эфира (kind=LIVESTREAM). При удалении комнаты — set null,
    // трансляция может продолжить жить без чата.
    chatRoomId: integer(),

    scheduledAt: timestamp({ precision: 3, mode: 'date' }),
    startedAt: timestamp({ precision: 3, mode: 'date' }),
    endedAt: timestamp({ precision: 3, mode: 'date' }),

    recordingEnabled: boolean().default(false).notNull(),
    // FK на файл записи (создаётся после окончания эфира media-пайплайном).
    recordingFileId: integer(),

    // FK на файл-обложку (thumbnail). nullable — необязательно.
    thumbnailFileId: integer(),

    // Аналитика аудитории.
    peakViewers: integer().default(0).notNull(),
    totalUniqueViewers: integer().default(0).notNull(),
    // Админский override maxViewers (null = без ограничения). Ручная
    // возможность выставить пик аудитории.
    maxViewersOverride: integer(),

    // Список внешних целей для рестрима (YouTube/Facebook/VK/OK/custom).
    // Каждый enabled-target → отдельный ffmpeg `-c copy` процесс при LIVE.
    // streamKey внутри — секрет (маскируется в admin UI, не логируется).
    externalRestreamTargets: jsonb().$type<ExternalRestreamTarget[] | null>(),

    // Фильтры аудитории в BroadcastFilters-стиле (null = для PUBLIC).
    // Shape: {
    //   languages?: string[],
    //   hasTelegram?: boolean,
    //   hasEmail?: boolean,
    //   registeredAfter?: string,  // ISO date
    //   registeredBefore?: string, // ISO date
    //   productIds?: string[],
    //   courseIds?: string[],
    //   channelIds?: string[],
    //   userIds?: number[],
    // }
    audienceFilters: jsonb(),

    // Языки, на которых показывается трансляция. Пустой массив = глобально
    // (показывается всем). Непустой = только юзерам с preferredLanguage ∈ languages.
    languages: text()
      .array()
      .notNull()
      .default(sql`ARRAY[]::text[]`),

    // FK на урок (ModuleItem), к которому привязывается запись стрима.
    // Позволяет после окончания эфира автоматически сохранить запись как
    // видео урока в модуле курса.
    linkedModuleItemId: integer(),

    // Причина отмены (если status=CANCELLED).
    cancelReason: text(),

    // Тестовый режим: эфир принимается MediaMTX для OBS-диагностики, но
    // не виден зрителям (плеер 403, list скрывает, broadcasts/triggers
    // не отправляются). Включается тумблером в админке до старта эфира.
    testMode: boolean().default(false).notNull(),

    createdAt: timestamp({ precision: 3, mode: 'date' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
    updatedAt: timestamp({ precision: 3, mode: 'date' })
      .$onUpdate(() => new Date())
      .notNull(),
  },
  (table) => [
    uniqueIndex('Livestream_streamKey_key').using('btree', table.streamKey.asc().nullsLast()),
    index('Livestream_status_idx').using('btree', table.status.asc().nullsLast()),
    index('Livestream_scheduledAt_idx').using('btree', table.scheduledAt.asc().nullsLast()),
    index('Livestream_hostAdminId_idx').using('btree', table.hostAdminId.asc().nullsLast()),
    index('Livestream_chatRoomId_idx').using('btree', table.chatRoomId.asc().nullsLast()),
    index('Livestream_thumbnailFileId_idx').using('btree', table.thumbnailFileId.asc().nullsLast()),
    // GIN для быстрой фильтрации по массиву languages (contains/overlap).
    index('Livestream_languages_idx').using('gin', table.languages),
    foreignKey({
      columns: [table.hostAdminId],
      foreignColumns: [adminUser.id],
      name: 'Livestream_hostAdminId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('restrict'),
    foreignKey({
      columns: [table.chatRoomId],
      foreignColumns: [chatRoom.id],
      name: 'Livestream_chatRoomId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('set null'),
    foreignKey({
      columns: [table.recordingFileId],
      foreignColumns: [file.id],
      name: 'Livestream_recordingFileId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('set null'),
    foreignKey({
      columns: [table.thumbnailFileId],
      foreignColumns: [file.id],
      name: 'Livestream_thumbnailFileId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('set null'),
    index('Livestream_linkedModuleItemId_idx').using('btree', table.linkedModuleItemId.asc().nullsLast()),
    foreignKey({
      columns: [table.linkedModuleItemId],
      foreignColumns: [moduleItem.id],
      name: 'Livestream_linkedModuleItemId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('set null'),
  ],
);

// ==========================================
// LIVESTREAM VIEWER SESSION — сессии просмотра (аналитика)
// ==========================================
// Одна запись = одно окно просмотра одного зрителя. Сессия активна, пока
// leftAt IS NULL. Heartbeat обновляет lastHeartbeatAt; по timeout сервис
// закрывает сессию (leftAt, watchedSeconds). `intervals` — массив
// {from: ISO, to: ISO} для восстановления точной истории просмотра.
// userId nullable: при GDPR-удалении юзера ссылка обнуляется, сама запись
// сохраняется для агрегированной аналитики (ipHash + country).
export const livestreamViewerSession = pgTable(
  'LivestreamViewerSession',
  {
    id: integer().primaryKey().generatedAlwaysAsIdentity(),

    livestreamId: integer().notNull(),
    userId: integer(),

    joinedAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
    lastHeartbeatAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
    leftAt: timestamp({ precision: 3, mode: 'date' }),

    watchedSeconds: integer().default(0).notNull(),
    // Массив интервалов просмотра: [{ from: ISO8601, to: ISO8601 }, ...].
    intervals: jsonb(),

    userAgent: text(),
    ipHash: text(),
    country: text(),

    createdAt: timestamp({ precision: 3, mode: 'date' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
  },
  (table) => [
    // Поиск активных сессий в рамках трансляции (leftAt IS NULL).
    index('LivestreamViewerSession_livestream_leftAt_idx').using(
      'btree',
      table.livestreamId.asc().nullsLast(),
      table.leftAt.asc().nullsLast(),
    ),
    // Одна активная сессия на юзера в эфире. Partial unique: только
    // для записей, где leftAt IS NULL. Завершённые сессии не участвуют.
    uniqueIndex('LivestreamViewerSession_livestream_user_active_key')
      .using('btree', table.livestreamId.asc().nullsLast(), table.userId.asc().nullsLast())
      .where(sql`"leftAt" IS NULL`),
    index('LivestreamViewerSession_userId_idx').using(
      'btree',
      table.userId.asc().nullsLast(),
    ),
    foreignKey({
      columns: [table.livestreamId],
      foreignColumns: [livestream.id],
      name: 'LivestreamViewerSession_livestreamId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('cascade'),
    foreignKey({
      columns: [table.userId],
      foreignColumns: [user.id],
      name: 'LivestreamViewerSession_userId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('set null'),
  ],
);

// ==========================================
// LIVESTREAM INTERACTION — голосования и сбор вопросов/значений
// ==========================================
export const livestreamInteraction = pgTable(
  'LivestreamInteraction',
  {
    id: integer().primaryKey().generatedAlwaysAsIdentity(),
    livestreamId: integer().notNull(),
    createdByAdminId: integer().notNull(),

    type: text().notNull(), // POLL | QUESTION_COLLECTION
    title: text().notNull(),
    description: text(),
    status: text().default('ACTIVE').notNull(), // ACTIVE | CLOSED

    isAnonymous: boolean().default(false).notNull(),
    allowMultipleOptions: boolean().default(false).notNull(),
    allowMultipleSubmissions: boolean().default(false).notNull(),
    needAnswer: boolean().default(false).notNull(),
    options: jsonb().$type<LivestreamInteractionOption[] | null>(),

    createdAt: timestamp({ precision: 3, mode: 'date' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
    updatedAt: timestamp({ precision: 3, mode: 'date' })
      .$onUpdate(() => new Date())
      .notNull(),
    closedAt: timestamp({ precision: 3, mode: 'date' }),
  },
  (table) => [
    index('LivestreamInteraction_livestream_status_idx').using(
      'btree',
      table.livestreamId.asc().nullsLast(),
      table.status.asc().nullsLast(),
    ),
    index('LivestreamInteraction_createdByAdminId_idx').using(
      'btree',
      table.createdByAdminId.asc().nullsLast(),
    ),
    foreignKey({
      columns: [table.livestreamId],
      foreignColumns: [livestream.id],
      name: 'LivestreamInteraction_livestreamId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('cascade'),
    foreignKey({
      columns: [table.createdByAdminId],
      foreignColumns: [adminUser.id],
      name: 'LivestreamInteraction_createdByAdminId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('restrict'),
  ],
);

export const livestreamInteractionResponse = pgTable(
  'LivestreamInteractionResponse',
  {
    id: integer().primaryKey().generatedAlwaysAsIdentity(),
    interactionId: integer().notNull(),
    userId: integer(),
    selectedOptionIds: text().array().notNull().default(sql`ARRAY[]::text[]`),
    textValue: text(),
    isAnswered: boolean().default(false).notNull(),
    telemetry: jsonb().$type<LivestreamInteractionTelemetry | null>(),
    createdAt: timestamp({ precision: 3, mode: 'date' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
    updatedAt: timestamp({ precision: 3, mode: 'date' })
      .$onUpdate(() => new Date())
      .notNull(),
  },
  (table) => [
    index('LivestreamInteractionResponse_interactionId_idx').using(
      'btree',
      table.interactionId.asc().nullsLast(),
    ),
    index('LivestreamInteractionResponse_userId_idx').using(
      'btree',
      table.userId.asc().nullsLast(),
    ),
    foreignKey({
      columns: [table.interactionId],
      foreignColumns: [livestreamInteraction.id],
      name: 'LivestreamInteractionResponse_interactionId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('cascade'),
    foreignKey({
      columns: [table.userId],
      foreignColumns: [user.id],
      name: 'LivestreamInteractionResponse_userId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('set null'),
  ],
);

export const livestreamNotificationSubscription = pgTable(
  'LivestreamNotificationSubscription',
  {
    id: integer().primaryKey().generatedAlwaysAsIdentity(),
    livestreamId: integer().notNull(),
    userId: integer().notNull(),
    isEnabled: boolean().default(true).notNull(),
    source: text().$type<LivestreamNotificationSubscriptionSource>().default('MANUAL').notNull(),
    createdAt: timestamp({ precision: 3, mode: 'date' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
    updatedAt: timestamp({ precision: 3, mode: 'date' })
      .$onUpdate(() => new Date())
      .notNull(),
  },
  (table) => [
    uniqueIndex('LivestreamNotificationSubscription_livestream_user_key').using(
      'btree',
      table.livestreamId.asc().nullsLast(),
      table.userId.asc().nullsLast(),
    ),
    index('LivestreamNotificationSubscription_livestream_enabled_idx').using(
      'btree',
      table.livestreamId.asc().nullsLast(),
      table.isEnabled.asc().nullsLast(),
    ),
    index('LivestreamNotificationSubscription_userId_idx').using(
      'btree',
      table.userId.asc().nullsLast(),
    ),
    index('LivestreamNotificationSubscription_user_source_idx').using(
      'btree',
      table.userId.asc().nullsLast(),
      table.source.asc().nullsLast(),
    ),
    foreignKey({
      columns: [table.livestreamId],
      foreignColumns: [livestream.id],
      name: 'LivestreamNotificationSubscription_livestreamId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('cascade'),
    foreignKey({
      columns: [table.userId],
      foreignColumns: [user.id],
      name: 'LivestreamNotificationSubscription_userId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('cascade'),
  ],
);

export const livestreamNotificationRule = pgTable(
  'LivestreamNotificationRule',
  {
    id: integer().primaryKey().generatedAlwaysAsIdentity(),
    livestreamId: integer().notNull(),
    minutesBefore: integer().notNull(),
    channels: notificationChannel()
      .array()
      .notNull()
      .default(sql`ARRAY['TELEGRAM']::"NotificationChannel"[]`),
    title: text().notNull(),
    messageTemplate: text().notNull(),
    button: jsonb().$type<LivestreamNotificationButton | null>(),
    localizedTemplates: jsonb().$type<Record<string, { title: string; messageTemplate: string; button: LivestreamNotificationButton | null }>>(),
    isActive: boolean().default(true).notNull(),
    sentAt: timestamp({ precision: 3, mode: 'date' }),
    lastError: text(),
    createdAt: timestamp({ precision: 3, mode: 'date' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
    updatedAt: timestamp({ precision: 3, mode: 'date' })
      .$onUpdate(() => new Date())
      .notNull(),
  },
  (table) => [
    index('LivestreamNotificationRule_livestreamId_idx').using(
      'btree',
      table.livestreamId.asc().nullsLast(),
    ),
    index('LivestreamNotificationRule_due_idx').using(
      'btree',
      table.isActive.asc().nullsLast(),
      table.sentAt.asc().nullsLast(),
      table.minutesBefore.asc().nullsLast(),
    ),
    foreignKey({
      columns: [table.livestreamId],
      foreignColumns: [livestream.id],
      name: 'LivestreamNotificationRule_livestreamId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('cascade'),
  ],
);
